import { Button } from '@/components/ui/button'
import * as Drawer from '@/components/ui/drawer'
import { IconButton } from '@/components/ui/icon-button'
import { Text } from '@/components/ui/text'
import { AppTooltip } from '@components'
import { A } from '@solidjs/router'
import { useAuth, user, userId } from '@stateApp'
import {
  LogOut,
  PanelLeft,
  Settings
} from 'lucide-solid'
import { For, Show, createSignal } from 'solid-js'
import { useService } from 'solid-services'
import { css } from 'styled-system/css'
// Added Box and Flex for the badge layout
import { Box, Divider, Flex, Grid } from 'styled-system/jsx'
import { grid } from 'styled-system/patterns'
import { Avatar } from '../../../features/Avatar'
// Imported the channels store for unread status
import { useChannels } from '../../../state/channels/channels'
import { DarkMode } from './DarkMode'
import Logo from './Logo'
import { adminRoutes, mainRoutes, settingRoutes } from './RouteSelector'
import StatusAlert from './StatusAlert'

// 2. Reusable NavLink Component
// Added optional hasBadge prop
const NavLink = (props: { href?: string; label: string; icon: any; onClick?: () => void; hasBadge?: boolean }) => (
  props.href
    ? (
      <A href={props.href} onClick={props.onClick}
        activeClass={css({
          '& button': {
            bg: 'jade.10',
            color: 'black'
          }
        })} end={props.href === '/'}>
        <Button
          p={3}
          width="100%"
          variant="plain"
          color='fg.subtle'
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Flex alignItems="center" gap="2">
            <Text fontSize='sm'>{props.label}</Text>
            {/* The little red dot indicator */}
            <Show when={props.hasBadge}>
              <Box width="2" height="2" bg="red.500" borderRadius="full" />
            </Show>
          </Flex>
          <props.icon />
        </Button>
      </A>
    )
    : (
      <Button
        p={3}
        width="100%"
        variant="plain"
        color='fg.subtle'
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={props.onClick}
      >
        <Flex alignItems="center" gap="2">
          <Text fontSize='md'>{props.label}</Text>
          {/* The little red dot indicator */}
          <Show when={props.hasBadge}>
            <Box width="2" height="2" bg="red.500" borderRadius="full" />
          </Show>
        </Flex>
        <props.icon />
      </Button>
    )
)

export default function SideBar() {
  const [isOpen, setIsOpen] = createSignal(false)
  const { isSuper, logout } = useService(useAuth)()

  // Consume our derived state
  const { hasUnreadChats } = useService(useChannels)()

  const closeDrawer = () => setIsOpen(false)

  return (
    <Drawer.Root size='xs' open={isOpen()} onOpenChange={({ open }) => setIsOpen(open)} placement='start'>
      <Drawer.Trigger asChild={(props => <>
        <IconButton {...props}
          size="sm"
          variant="plain"
          color='fg.subtle'><PanelLeft /></IconButton>
      </>)} />
      <Drawer.Backdrop />
      <Drawer.Positioner>
        <Drawer.Content>
          <Drawer.Body class={grid({ p: 3, alignContent: 'space-between', gridTemplateRows: '1fr auto' })}>

            <Grid alignContent="space-between" h="100%">
              <Grid>
                <Grid gridTemplateColumns="1fr 40px">
                  <Logo />
                  <AppTooltip content={user()?.user.email}>
                    <Avatar id={userId()} />
                  </AppTooltip>
                </Grid>
                <Divider />

                <Grid p={1}>
                  <For each={mainRoutes}>
                    {(route) => (
                      <NavLink
                        {...route}
                        onClick={closeDrawer}
                        // Automatically flag the Chat route if there are unreads
                        hasBadge={route.label === 'Chat' && hasUnreadChats()}
                      />
                    )}
                  </For>
                </Grid>
              </Grid>


              <Grid p={1}>
                <Show when={isSuper()}>
                  <For each={adminRoutes}>
                    {(route) => <NavLink {...route} onClick={closeDrawer} />}
                  </For>
                </Show>
                <For each={settingRoutes}>
                  {(route) => <NavLink {...route} onClick={closeDrawer} />}
                </For>
              </Grid>
            </Grid>

            <Grid>
              <StatusAlert setIsOpen={setIsOpen} />
              <Divider />
              <NavLink
                label="Logout"
                icon={() => <LogOut size={24} />}
                onClick={logout}
              />
            </Grid>

          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  )
}
